import { Injectable } from '@angular/core';
import { Actions, createEffect, EffectsModule, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { UserStoreService } from '@polaris-customerweb/sharedcomponents.core';
import { GarageItemsService } from '@polaris-customerweb/sharedcomponents.garage';
import {
    catchError,
    concatMap,
    exhaustMap,
    filter,
    switchMap,
    withLatestFrom,
} from 'rxjs/operators';
import { AppState } from 'src/app/models/app-state';
import * as EquipmentActions from '../equipment/equipment.actions';
import { VehicleLocationActions } from '../vehicle/vehicle-location/vehicle-location.actions';
import * as VehicleActions from '../vehicle/vehicle.actions';
import * as GarageActions from './garage.actions';
import { GarageActionTypes } from './garage.actions';
import { selectGarageItems, selectGarageItemsLoaded } from './garage.selectors';

@Injectable()
export class GarageEffects {
    readonly className = 'GarageEffects';

    getGarageItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                GarageActionTypes.getGarageItems,
                VehicleLocationActions.getAllVehicleLocations
            ),
            withLatestFrom(this.store.select(selectGarageItemsLoaded)),
            filter(([, garageLoaded]) => !garageLoaded),
            exhaustMap(() =>
                this.garageItemsService
                    .getGarageItemsOnUserAuthenticated$()
                    .pipe(
                        switchMap(([vehicles, equipment]) => {
                            return [
                                VehicleActions.GetVinInfo({
                                    vehicles,
                                }),
                                EquipmentActions.GetEquipmentSuccess({
                                    equipment,
                                }),
                            ];
                        }),
                        catchError((errorMessage) => {
                            return [
                                GarageActions.GetGarageItemsFail({
                                    errorMessage,
                                }),
                            ];
                        })
                    )
            )
        )
    );

    setActiveGarageItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GarageActionTypes.setActiveGarageItemId),
            withLatestFrom(this.store.select(selectGarageItems)),
            concatMap(([{ garageItemId }, garageItems]) => {
                const garageItem = garageItems.find(
                    (veh) => veh.id === garageItemId
                );
                const actions: Action[] = [];
                if (garageItem) {
                    if (garageItem.isVehicle) {
                        actions.push(
                            VehicleActions.SetActiveVehicleId({
                                vehicleId: garageItem.id,
                            })
                        );
                    } else {
                        actions.push(
                            EquipmentActions.SetActiveEquipmentId({
                                equipmentId: garageItem.id,
                            })
                        );
                    }
                    this.userStoreService.set(
                        'activeGarageItemId',
                        garageItem.id
                    );
                } else {
                    actions.push(
                        VehicleActions.SetActiveVehicleId({
                            vehicleId: '',
                        })
                    );
                }
                return actions;
            })
        )
    );

    constructor(
        private readonly actions$: Actions,
        private readonly garageItemsService: GarageItemsService,
        private userStoreService: UserStoreService,
        private store: Store<AppState>
    ) {}
}

export const garageEffectsModule = EffectsModule.forFeature([GarageEffects]);
